import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IDoesFilterPassParams, IRowNode } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import {
  BulkSheetFilter,
  FilterFooter,
  FilterInput,
} from '../../../containers/BulkSheetView/components/filter/common'
import {
  SelectFilterArea,
  SelectFilterFormControl,
  CheckboxArea,
  FilterLabel,
} from '../../../containers/BulkSheetView/components/filter/SelectFilter'
import { Checkbox, Divider, Typography, styled } from '@mui/material'
import { intl } from '../../../../i18n'
import {
  ProductBacklogRow,
  SprintRow,
  isSprintRow,
  isProductBacklogItemRow,
} from '../productBacklog'
import {
  getWbsItemStatusColorCode,
  getWbsItemStatusDeepColorCode,
} from '../../../../lib/functions/wbsItem'
import { SprintStatus } from '../../../../lib/functions/sprint'
import { WbsItemStatus } from '../../../../domain/entity/WbsItemEntity'

export type StatusFilterValue = {
  status: string
  backgroundColor: string
  type: 'sprint' | 'wbsItem'
}

type Props = CustomFilterProps & {
  getLabel: (option: StatusFilterValue) => string
  sortValues: (options: StatusFilterValue[]) => StatusFilterValue[]
}

type FilterModel = {
  sprintValues: string[]
  pbiValues: string[]
  includeBlank: boolean
  includeNoSprint: boolean
}

const SectionLabel = styled(Typography)({
  fontSize: '11px',
  fontWeight: 'bold',
  color: '#666',
  marginTop: '8px',
  marginBottom: '4px',
})

const getSprintStatusColor = (status: SprintStatus): string => {
  switch (status) {
    case SprintStatus.INPROGRESS:
      return getWbsItemStatusColorCode(WbsItemStatus.DOING)
    case SprintStatus.FINISHED:
      return getWbsItemStatusColorCode(WbsItemStatus.DONE)
    case SprintStatus.STANDBY:
      return getWbsItemStatusDeepColorCode(WbsItemStatus.TODO)
    case SprintStatus.CANCELED:
      return getWbsItemStatusColorCode(WbsItemStatus.DISCARD)
  }
}

/**
 * ノードからステータスフィルター用の値を取得する
 */
const getStatusFilterValue = (
  node: IRowNode<ProductBacklogRow>
): StatusFilterValue | StatusFilterValue[] | undefined => {
  const data = node?.data
  if (!data) return undefined

  if (isSprintRow(data)) {
    const sprintStatus = data.sprintDetail?.status
    if (!sprintStatus) return undefined
    return {
      status: sprintStatus,
      backgroundColor: getSprintStatusColor(sprintStatus),
      type: 'sprint' as const,
    }
  }

  if (isProductBacklogItemRow(data)) {
    const parentNode = node?.parent
    const parentSprintRow = parentNode?.data as SprintRow | undefined
    const parentSprintStatus = parentSprintRow?.sprintDetail?.status

    const wbsStatus = data.wbsItem.status
    const result: StatusFilterValue[] = []

    if (parentSprintStatus) {
      result.push({
        status: parentSprintStatus,
        backgroundColor: getSprintStatusColor(parentSprintStatus),
        type: 'sprint',
      })
    }

    if (wbsStatus) {
      result.push({
        status: wbsStatus,
        backgroundColor: getWbsItemStatusDeepColorCode(wbsStatus),
        type: 'wbsItem',
      })
    }

    return result.length > 0 ? result : undefined
  }

  return undefined
}

/**
 * ProductBacklog専用のステータスフィルター
 * SprintステータスとPBIステータスを別セクションで表示し、独立して選択可能
 */
export const ProductBacklogStatusFilter = ({
  model,
  onModelChange,
  api,
  getLabel,
  sortValues,
}: Props) => {
  // UI-local state
  const [text, setText] = useState('')
  const [options, setOptions] = useState<StatusFilterValue[]>([])
  const initialized = useRef(false)

  // Derive state from model (single source of truth)
  const filterModel = model as FilterModel | null

  // オプションをタイプ別に分類
  const sprintOptions = useMemo(
    () => options.filter(o => o.type === 'sprint'),
    [options]
  )
  const pbiOptions = useMemo(
    () => options.filter(o => o.type === 'wbsItem'),
    [options]
  )

  const sprintValues =
    filterModel?.sprintValues ?? sprintOptions.map(o => o.status)
  const pbiValues = filterModel?.pbiValues ?? pbiOptions.map(o => o.status)
  const includeBlank = filterModel?.includeBlank ?? true
  const includeNoSprint = filterModel?.includeNoSprint ?? true

  // フィルターがアクティブかどうか
  const isFilterActive =
    filterModel !== null &&
    !(
      sprintValues.length === sprintOptions.length &&
      pbiValues.length === pbiOptions.length &&
      includeBlank &&
      includeNoSprint
    )

  // 全選択/全解除のヘルパー
  const isSprintAllSelected = useMemo(
    () =>
      sprintOptions.length > 0 && sprintValues.length === sprintOptions.length,
    [sprintValues, sprintOptions]
  )
  const isPbiAllSelected = useMemo(
    () => pbiOptions.length > 0 && pbiValues.length === pbiOptions.length,
    [pbiValues, pbiOptions]
  )

  const buildModel = useCallback(
    (
      newSprintValues: string[],
      newPbiValues: string[],
      newIncludeBlank: boolean,
      newIncludeNoSprint: boolean
    ): FilterModel | null => {
      const allSprintSelected =
        newSprintValues.length === sprintOptions.length ||
        sprintOptions.length === 0
      const allPbiSelected =
        newPbiValues.length === pbiOptions.length || pbiOptions.length === 0
      if (
        allSprintSelected &&
        allPbiSelected &&
        newIncludeBlank &&
        newIncludeNoSprint
      ) {
        return null
      }
      return {
        sprintValues: newSprintValues,
        pbiValues: newPbiValues,
        includeBlank: newIncludeBlank,
        includeNoSprint: newIncludeNoSprint,
      }
    },
    [sprintOptions.length, pbiOptions.length]
  )

  const getOptions = useCallback((): StatusFilterValue[] => {
    const initialOptions: (StatusFilterValue | StatusFilterValue[])[] = []
    api.forEachNode(node => {
      const value = getStatusFilterValue(node)
      if (value) {
        initialOptions.push(value)
      }
    })

    const newOptionValues: string[] = []
    const newOptions: StatusFilterValue[] = []

    initialOptions.forEach(option => {
      if (Array.isArray(option)) {
        option.forEach(item => {
          const key = `${item.type}-${item.status}`
          if (!newOptionValues.includes(key)) {
            newOptionValues.push(key)
            newOptions.push(item)
          }
        })
      } else {
        const key = `${option.type}-${option.status}`
        if (!newOptionValues.includes(key)) {
          newOptionValues.push(key)
          newOptions.push(option)
        }
      }
    })

    return sortValues ? sortValues(newOptions) : newOptions
  }, [api, sortValues])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data?.isAdded) return true

      const selectValue = getStatusFilterValue(params.node)

      // スプリント未設定の親行の場合はincludeNoSprintで判定
      if (!selectValue && params.data?.rowType === 'sprint') {
        return includeNoSprint
      }

      if (includeBlank && !selectValue) return true
      if (!selectValue) return false

      if (Array.isArray(selectValue)) {
        const sprintStatus = selectValue.find(v => v.type === 'sprint')?.status
        const pbiStatus = selectValue.find(v => v.type === 'wbsItem')?.status

        const sprintPass = sprintStatus
          ? sprintValues.includes(sprintStatus)
          : includeNoSprint
        const pbiPass = !pbiStatus || pbiValues.includes(pbiStatus)

        return sprintPass && pbiPass
      }

      if (selectValue.type === 'sprint') {
        return sprintValues.includes(selectValue.status)
      }
      return includeNoSprint && pbiValues.includes(selectValue.status)
    },

    afterGuiAttached() {
      setOptions(getOptions())
    },

    getModelAsString() {
      if (!filterModel) return ''
      const labels: string[] = []
      if (includeNoSprint) {
        labels.push(
          intl.formatMessage({
            id: 'productBacklog.estimatesByAssignee.sprintUnassigned',
          })
        )
      }

      sprintOptions
        .filter(o => sprintValues.includes(o.status))
        .forEach(o => labels.push(getLabel(o)))

      pbiOptions
        .filter(o => pbiValues.includes(o.status))
        .forEach(o => labels.push(getLabel(o)))

      return labels.join(',')
    },
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行
  useEffect(() => {
    setOptions(getOptions())
  }, [])

  // オプションが取得された後の初期化処理
  useEffect(() => {
    if (!initialized.current && options.length > 0 && !model) {
      // 初回のみ: model が null（復元なし）の場合は何もしない（全選択状態）
      initialized.current = true
    } else if (model && options.length > 0) {
      // 復元時: オプションが読み込まれたら初期化完了
      initialized.current = true
    }
  }, [options, model])

  // 検索フィルター
  const filteredSprintOptions = sprintOptions.filter(option => {
    const label = getLabel(option)
    return !text || (label && label.toLowerCase().includes(text.toLowerCase()))
  })

  const filteredPbiOptions = pbiOptions.filter(option => {
    const label = getLabel(option)
    return !text || (label && label.toLowerCase().includes(text.toLowerCase()))
  })

  return (
    <BulkSheetFilter>
      <FilterInput
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={'Search...'}
        sx={{ height: 22 }}
      />
      <SelectFilterArea>
        {/* Sprint Status Section */}
        <SectionLabel>Sprint Status</SectionLabel>
        <SelectFilterFormControl
          label="(Select All)"
          control={
            <Checkbox
              checked={isSprintAllSelected && includeNoSprint}
              indeterminate={
                !(isSprintAllSelected && includeNoSprint) &&
                (sprintValues.length > 0 || includeNoSprint)
              }
              onChange={e => {
                if (e.target.checked) {
                  onModelChange(
                    buildModel(
                      sprintOptions.map(o => o.status),
                      pbiValues,
                      includeBlank,
                      true
                    )
                  )
                } else {
                  onModelChange(buildModel([], pbiValues, includeBlank, false))
                }
              }}
              disableRipple={true}
            />
          }
        />
        <CheckboxArea>
          {filteredSprintOptions.map(option => {
            const value = option.status
            return (
              <SelectFilterFormControl
                key={`sprint-filter-${value}`}
                label={
                  <FilterLabel
                    sx={{
                      backgroundColor: option.backgroundColor ?? 'inherit',
                    }}
                  >
                    <Typography sx={{ padding: '0 3px' }}>
                      {getLabel(option)}
                    </Typography>
                  </FilterLabel>
                }
                control={
                  <Checkbox
                    checked={sprintValues.includes(value)}
                    onChange={e => {
                      const newSprintValues = e.target.checked
                        ? [...sprintValues, value]
                        : sprintValues.filter(v => v !== value)
                      onModelChange(
                        buildModel(
                          newSprintValues,
                          pbiValues,
                          includeBlank,
                          includeNoSprint
                        )
                      )
                    }}
                    disableRipple={true}
                  />
                }
              />
            )
          })}
          {/* No Sprint option - at bottom */}
          {!text && (
            <SelectFilterFormControl
              label={intl.formatMessage({
                id: 'productBacklog.estimatesByAssignee.sprintUnassigned',
              })}
              control={
                <Checkbox
                  checked={includeNoSprint}
                  onChange={e =>
                    onModelChange(
                      buildModel(
                        sprintValues,
                        pbiValues,
                        includeBlank,
                        e.target.checked
                      )
                    )
                  }
                  disableRipple={true}
                />
              }
            />
          )}
        </CheckboxArea>

        {/* Divider */}
        {filteredSprintOptions.length > 0 && filteredPbiOptions.length > 0 && (
          <Divider sx={{ my: 1 }} />
        )}

        {/* PBI Status Section */}
        {filteredPbiOptions.length > 0 && (
          <>
            <SectionLabel>PBI Status</SectionLabel>
            <SelectFilterFormControl
              label="(Select All)"
              control={
                <Checkbox
                  checked={isPbiAllSelected}
                  indeterminate={!isPbiAllSelected && pbiValues.length > 0}
                  onChange={e => {
                    const newPbiValues = e.target.checked
                      ? pbiOptions.map(o => o.status)
                      : []
                    onModelChange(
                      buildModel(
                        sprintValues,
                        newPbiValues,
                        includeBlank,
                        includeNoSprint
                      )
                    )
                  }}
                  disableRipple={true}
                />
              }
            />
            <CheckboxArea>
              {filteredPbiOptions.map(option => {
                const value = option.status
                return (
                  <SelectFilterFormControl
                    key={`pbi-filter-${value}`}
                    label={
                      <FilterLabel
                        sx={{
                          backgroundColor: option.backgroundColor ?? 'inherit',
                        }}
                      >
                        <Typography sx={{ padding: '0 3px' }}>
                          {getLabel(option)}
                        </Typography>
                      </FilterLabel>
                    }
                    control={
                      <Checkbox
                        checked={pbiValues.includes(value)}
                        onChange={e => {
                          const newPbiValues = e.target.checked
                            ? [...pbiValues, value]
                            : pbiValues.filter(v => v !== value)
                          onModelChange(
                            buildModel(
                              sprintValues,
                              newPbiValues,
                              includeBlank,
                              includeNoSprint
                            )
                          )
                        }}
                        disableRipple={true}
                      />
                    }
                  />
                )
              })}
            </CheckboxArea>
          </>
        )}
      </SelectFilterArea>
      <FilterFooter loading={false} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}

ProductBacklogStatusFilter.displayName = 'ProductBacklogStatusFilter'
